// pkg72b — teal CTA button(s) #1a7a5e → NAVY #1E2761; semantic status dots/greens kept — delivered 2026-07-12
// pkg72 — LawMighty retheme: brand hex sweep (#1a1a1a→#0F1838 NAVY_DEEP, #0f0f0f→#0A1028, #f8f8f8→#F5F1E8 CREAM, #c0392b→#B85042 TERRA) — delivered 2026-07-12
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

// Per-response question-by-question workspace for the client. Structurally
// mirrors AdminDiscoveryResponseWorkspacePage but stripped of everything the
// client shouldn't have:
//   - No Approved checkbox (attorney concept)
//   - No attorney_notes (internal-only)
//   - No question text/type/number editing
//   - No add / delete / reorder controls
//
// What the client gets:
//   - Read the question
//   - Edit the answer text
//   - Save per question (with optimistic locking)
//   - See "Edited by your attorney" when the most recent edit was by admin
//   - See a soft indicator if the attorney is currently viewing the same question
//   - Workspace is read-only when status === 'locked'
//   - If the attorney flips the status back to 'draft' mid-session, the
//     save returns 403 NOT_WRITABLE and we close the workspace gracefully.

const TYPE_LABELS = {
  interrogatory: 'Interrogatory',
  rfp:           'Request for Production',
  rfa:           'Request for Admission',
};

const PRESENCE_HEARTBEAT_MS = 10000; // every 10s while editing
const PRESENCE_FRESH_MS     = 30000; // anything older than this is "gone"

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function ClientDiscoveryWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [response,  setResponse]  = useState(null);
  const [questions, setQuestions] = useState([]);
  const [activeQid, setActiveQid] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  // Active question's draft state (just answer_text + version for the lock).
  const [draft, setDraft] = useState({ answer_text: '', version: 0 });
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Presence (other party currently viewing). Keyed by question_id → array
  // of presence rows. We strip out our own rows on read.
  const [presenceByQ, setPresenceByQ] = useState({});

  // Conflict modal state. When a save returns 409, we open this with the
  // user's local draft + the current server row, and let them pick.
  const [conflict, setConflict] = useState(null);
  // conflict shape: { localText, serverRow, mergeText: '', mode: 'pick' | 'merge' }

  // ─── LOAD ─────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const data = await api.getClientDiscovery(id);
      setResponse(data.response);
      setQuestions(data.questions || []);
      // Default to the first question if none selected, or re-anchor if the
      // currently-active one disappeared somehow (shouldn't happen, but safe).
      if (data.questions.length && (!activeQid || !data.questions.find(q => q.id === activeQid))) {
        setActiveQid(data.questions[0].id);
      } else if (!data.questions.length) {
        setActiveQid(null);
      }
    } catch (err) {
      setError(err.message || 'Could not load this discovery response.');
    } finally {
      setLoading(false);
    }
  }, [id, activeQid]);

  useEffect(() => { load(); }, [id]);

  // When activeQid changes, sync draft from the loaded question.
  useEffect(() => {
    if (!activeQid) return;
    const q = questions.find(x => x.id === activeQid);
    if (!q) return;
    setDraft({ answer_text: q.answer_text || '', version: q.version });
    setHasUnsavedChanges(false);
  }, [activeQid, questions]);

  // Warn on tab close if there are unsaved changes.
  useEffect(() => {
    function before(e) {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    }
    window.addEventListener('beforeunload', before);
    return () => window.removeEventListener('beforeunload', before);
  }, [hasUnsavedChanges]);

  // ─── PRESENCE ─────────────────────────────────────────────────────────────

  // Heartbeat + poll. Both run every PRESENCE_HEARTBEAT_MS while a question
  // is active and the response is in 'visible_to_client' (no point
  // signaling presence on a locked response — nobody can edit anyway).
  const heartbeatTimer = useRef(null);
  const pollTimer      = useRef(null);

  useEffect(() => {
    if (!activeQid || !response || response.status !== 'visible_to_client') return;

    let cancelled = false;

    async function beat() {
      try { await api.sendClientDiscoveryPresence(id, activeQid); } catch (_) { /* silent */ }
    }
    async function poll() {
      try {
        const rows = await api.getClientDiscoveryPresence(id);
        if (cancelled) return;
        // Bucket by question; filter out stale and own (client) rows.
        const cutoff = Date.now() - PRESENCE_FRESH_MS;
        const byQ = {};
        for (const r of rows || []) {
          if (r.user_type === 'client') continue; // hide self (we don't need to see ourselves)
          const t = new Date(r.last_seen_at).getTime();
          if (isNaN(t) || t < cutoff) continue;
          (byQ[r.question_id] = byQ[r.question_id] || []).push(r);
        }
        setPresenceByQ(byQ);
      } catch (_) { /* silent */ }
    }

    // Fire immediately on activation, then on an interval.
    beat(); poll();
    heartbeatTimer.current = setInterval(beat, PRESENCE_HEARTBEAT_MS);
    pollTimer.current      = setInterval(poll, PRESENCE_HEARTBEAT_MS);

    return () => {
      cancelled = true;
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      if (pollTimer.current)      clearInterval(pollTimer.current);
      heartbeatTimer.current = null;
      pollTimer.current      = null;
    };
  }, [id, activeQid, response]);

  // ─── SAVE ─────────────────────────────────────────────────────────────────

  async function saveAnswer() {
    const q = questions.find(x => x.id === activeQid);
    if (!q) return;
    setSaving(true); setError('');
    try {
      const updated = await api.saveClientDiscoveryAnswer(id, q.id, {
        answer_text: draft.answer_text,
        version: draft.version,
      });
      setQuestions(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
      setDraft(p => ({ ...p, version: updated.version }));
      setHasUnsavedChanges(false);
    } catch (err) {
      if (err.status === 409 && err.current) {
        // Conflict: open the modal with both versions.
        setConflict({
          localText: draft.answer_text,
          serverRow: err.current,
          mergeText: err.current.answer_text || '',
          mode: 'pick',
        });
      } else if (err.status === 403 && err.code === 'NOT_WRITABLE') {
        // The attorney flipped status back to draft (or locked) while we
        // were editing. Reload to surface the new status; the UI will
        // become read-only / kick back to the list if it's now invisible.
        setError('Your attorney has changed this response\u2019s status. Reloading...');
        setTimeout(load, 800);
      } else {
        setError(err.message || 'Could not save your answer.');
      }
    } finally {
      setSaving(false);
    }
  }

  // ─── CONFLICT MODAL ACTIONS ───────────────────────────────────────────────

  async function resolveKeepMine() {
    // Force-save our local text against the new server version.
    if (!conflict) return;
    setSaving(true); setError('');
    try {
      const updated = await api.saveClientDiscoveryAnswer(id, activeQid, {
        answer_text: conflict.localText,
        version: conflict.serverRow.version,
      });
      setQuestions(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
      setDraft({ answer_text: updated.answer_text || '', version: updated.version });
      setHasUnsavedChanges(false);
      setConflict(null);
    } catch (err) {
      setError(err.message || 'Could not save your answer.');
    } finally {
      setSaving(false);
    }
  }

  function resolveKeepTheirs() {
    if (!conflict) return;
    // Discard local edits; adopt server version.
    const srv = conflict.serverRow;
    setQuestions(prev => prev.map(x => x.id === srv.id ? { ...x, ...srv } : x));
    setDraft({ answer_text: srv.answer_text || '', version: srv.version });
    setHasUnsavedChanges(false);
    setConflict(null);
  }

  async function resolveMerge() {
    if (!conflict) return;
    setSaving(true); setError('');
    try {
      const updated = await api.saveClientDiscoveryAnswer(id, activeQid, {
        answer_text: conflict.mergeText,
        version: conflict.serverRow.version,
      });
      setQuestions(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
      setDraft({ answer_text: updated.answer_text || '', version: updated.version });
      setHasUnsavedChanges(false);
      setConflict(null);
    } catch (err) {
      // If the merge save also conflicts (someone edited between modal open
      // and Save Merge click), update the modal with the new server row.
      if (err.status === 409 && err.current) {
        setConflict(prev => prev ? { ...prev, serverRow: err.current } : null);
        setError('Someone edited this again while you were merging. Latest version loaded \u2014 try again.');
      } else if (err.status === 403 && err.code === 'NOT_WRITABLE') {
        setError('Your attorney has changed this response\u2019s status. Reloading...');
        setConflict(null);
        setTimeout(load, 800);
      } else {
        setError(err.message || 'Could not save your answer.');
      }
    } finally {
      setSaving(false);
    }
  }

  // ─── DERIVED ──────────────────────────────────────────────────────────────

  const activeQuestion = activeQid ? questions.find(x => x.id === activeQid) : null;
  const isLocked = response?.status === 'locked';
  const isReadOnly = isLocked; // future-proof: keep them separate names

  function setAnswerText(v) {
    setDraft(p => ({ ...p, answer_text: v }));
    setHasUnsavedChanges(true);
  }

  // ─── RENDER ───────────────────────────────────────────────────────────────

  if (loading) {
    return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  }
  if (!response) {
    return (
      <div style={{ padding: 24 }}>
        <button className="btn btn-sm" onClick={() => navigate('/discovery')}>← Back</button>
        <div className="alert alert-error" style={{ marginTop: 16 }}>This discovery response is no longer available.</div>
      </div>
    );
  }

  // For the active question header, decide whether to render the "edited by
  // your attorney" marker. It applies whenever the most recent edit came
  // from the admin side.
  const editedByAttorney = activeQuestion?.answer_updated_by_type === 'admin';
  // Presence on the *active* question, attributed only to admin viewers (we
  // already strip client/self in the poll).
  const adminViewersHere = (presenceByQ[activeQid] || []).filter(p => p.user_type === 'admin');

  return (
    <div>
      <button className="btn btn-sm" onClick={() => navigate('/discovery')} style={{ marginBottom: 12 }}>
        ← Back to all responses
      </button>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
        <div style={{ minWidth: 0, flex: '1 1 400px' }}>
          <h1 className="page-title" style={{ marginBottom: 4 }}>
            {response.title || 'Discovery Response'}
          </h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            {response.plaintiff && response.defendant && (
              <span>{response.plaintiff} v. {response.defendant}</span>
            )}
            {response.docket_no && <span style={{ color: '#888' }}> · Docket {response.docket_no}</span>}
          </p>
        </div>
        {isLocked && (
          <div style={{
            background: '#f0f0f0', color: '#555', border: '1px solid #ddd',
            padding: '6px 12px', borderRadius: 4, fontSize: 12, fontFamily: 'Arial, sans-serif',
            fontWeight: 600,
          }}>
            🔒 This response is closed
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isLocked && (
        <div className="alert" style={{
          background: '#f7f7f7', color: '#555', border: '1px solid #ddd',
          padding: 10, borderRadius: 4, marginBottom: 14, fontFamily: 'Arial, sans-serif', fontSize: 12,
        }}>
          This response has been closed by your attorney. You can review the questions and answers
          but cannot make changes. Contact your attorney if something needs to be updated.
        </div>
      )}

      {/* Helpful intro */}
      {!isLocked && (
        <div className="alert" style={{
          background: '#e8f0fb', color: '#2f5496', border: '1px solid #bcd4ee',
          padding: 10, borderRadius: 4, marginBottom: 14, fontFamily: 'Arial, sans-serif', fontSize: 12,
        }}>
          Read each question and type your answer. Click <strong>Save Answer</strong> when you're done with each one.
          Your attorney can see your answers as you save them.
        </div>
      )}

      {/* Two-pane layout */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 280px) 1fr', gap: 16 }}>
        {/* Left rail: read-only question list */}
        <div className="card" style={{ padding: 0, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
          <div style={{
            padding: '10px 12px', borderBottom: '1px solid #eee', background: '#fafafa',
            fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888',
            textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600,
          }}>
            Questions ({questions.length})
          </div>
          {questions.length === 0 ? (
            <div style={{ padding: 16, fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888' }}>
              No questions in this response.
            </div>
          ) : (
            <div>
              {questions.map((q, idx) => {
                const isActive = q.id === activeQid;
                const attorneyHere = (presenceByQ[q.id] || []).some(p => p.user_type === 'admin');
                const attorneyEdited = q.answer_updated_by_type === 'admin';
                return (
                  <div
                    key={q.id}
                    onClick={() => setActiveQid(q.id)}
                    style={{
                      padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      background: isActive ? '#fffbe6' : '#fff',
                      borderLeft: isActive ? '3px solid #b7870a' : '3px solid transparent',
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 2,
                        background: '#0F1838', color: '#fff', fontFamily: 'Arial, sans-serif',
                      }}>{idx + 1}</span>
                      <span style={{ fontSize: 10, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                        {TYPE_LABELS[q.question_type] || q.question_type}
                        {q.question_number ? ` ${q.question_number}` : ''}
                      </span>
                      {attorneyHere && (
                        <span title="Your attorney is viewing this question right now"
                          style={{
                            fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 2,
                            background: '#1E2761', color: '#fff', fontFamily: 'Arial, sans-serif',
                            marginLeft: 'auto',
                          }}>● ATTY</span>
                      )}
                    </div>
                    <div style={{
                      fontFamily: 'Times New Roman, serif', fontSize: 12, color: '#333',
                      lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {q.question_text}
                    </div>
                    {attorneyEdited && (
                      <div style={{ fontSize: 10, color: '#b7870a', fontFamily: 'Arial, sans-serif', marginTop: 4, fontWeight: 600 }}>
                        Edited by your attorney
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right pane: active question */}
        <div>
          {!activeQuestion ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: '#888', fontFamily: 'Arial, sans-serif', fontSize: 13 }}>
              {questions.length === 0
                ? 'No questions in this response.'
                : 'Select a question on the left to begin.'}
            </div>
          ) : (
            <>
              {/* Question card (read-only display) */}
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  <div className="card-title" style={{ fontSize: 14, margin: 0 }}>
                    Question {questions.findIndex(q => q.id === activeQid) + 1} of {questions.length}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif' }}>
                    {TYPE_LABELS[activeQuestion.question_type] || activeQuestion.question_type}
                    {activeQuestion.question_number ? ` · ${activeQuestion.question_number}` : ''}
                    {draft.version > 0 && <span> · v{draft.version}</span>}
                  </div>
                </div>

                {adminViewersHere.length > 0 && (
                  <div style={{
                    marginBottom: 10, padding: '6px 10px', borderRadius: 4,
                    background: '#e8f5ee', color: '#1a7a5e', border: '1px solid #b8dcc6',
                    fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1a7a5e', display: 'inline-block' }} />
                    Your attorney is viewing this question right now
                    {adminViewersHere[0].user_name && <span style={{ color: '#1a7a5e', fontWeight: 400 }}> ({adminViewersHere[0].user_name})</span>}
                  </div>
                )}

                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: 6, fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
                    The question
                  </label>
                  <div style={{
                    fontFamily: 'Times New Roman, serif', fontSize: 14, lineHeight: 1.6,
                    padding: 12, background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                  }}>
                    {activeQuestion.question_text}
                  </div>
                </div>
              </div>

              {/* Answer card */}
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div className="card-title" style={{ fontSize: 14 }}>Your Answer</div>
                  {editedByAttorney && (
                    <div style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3,
                      background: '#fff5db', color: '#b7870a', border: '1px solid #f0d68a',
                      fontFamily: 'Arial, sans-serif',
                    }}>
                      Edited by your attorney
                    </div>
                  )}
                </div>
                {activeQuestion.answer_updated_at && (
                  <div style={{ fontSize: 11, color: '#888', fontFamily: 'Arial, sans-serif', marginTop: -6, marginBottom: 10 }}>
                    Last updated {formatDateTime(activeQuestion.answer_updated_at)}
                  </div>
                )}
                <div className="form-group">
                  <textarea
                    value={draft.answer_text}
                    onChange={e => setAnswerText(e.target.value)}
                    rows={10}
                    disabled={isReadOnly}
                    placeholder={isReadOnly ? '' : 'Type your answer here. Be as complete and accurate as you can — your attorney will review what you write.'}
                    style={{
                      width: '100%', fontFamily: 'Times New Roman, serif', fontSize: 14,
                      padding: 10, lineHeight: 1.5,
                      background: isReadOnly ? '#f7f7f7' : '#fff',
                      color: isReadOnly ? '#666' : '#0F1838',
                    }} />
                </div>
                {!isReadOnly && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, color: hasUnsavedChanges ? '#b7870a' : '#888', fontFamily: 'Arial, sans-serif' }}>
                      {hasUnsavedChanges ? 'You have unsaved changes.' : 'All changes saved.'}
                    </div>
                    <button className="btn btn-primary" onClick={saveAnswer} disabled={saving || !hasUnsavedChanges}>
                      {saving ? <><span className="spinner" />Saving...</> : (hasUnsavedChanges ? 'Save Answer' : 'Saved')}
                    </button>
                  </div>
                )}
              </div>

              {/* Nav between questions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                <button className="btn" disabled={questions.findIndex(q => q.id === activeQid) === 0}
                  onClick={() => {
                    const idx = questions.findIndex(q => q.id === activeQid);
                    if (idx > 0) setActiveQid(questions[idx - 1].id);
                  }}>← Previous Question</button>
                <button className="btn" disabled={questions.findIndex(q => q.id === activeQid) === questions.length - 1}
                  onClick={() => {
                    const idx = questions.findIndex(q => q.id === activeQid);
                    if (idx < questions.length - 1) setActiveQid(questions[idx + 1].id);
                  }}>Next Question →</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Conflict modal */}
      {conflict && (
        <div onClick={() => !saving && setConflict(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 6, maxWidth: 720, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 20 }}>
            <h2 style={{ fontFamily: 'Times New Roman, serif', fontSize: 22, margin: '0 0 4px' }}>
              Someone else edited this answer
            </h2>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#666', marginBottom: 16 }}>
              While you were typing, your attorney saved a new version of this answer. Choose which version
              to keep, or combine them yourself.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
                  Your version
                </div>
                <div style={{
                  padding: 10, border: '1px solid #f0d68a', background: '#fff8e1', borderRadius: 4,
                  fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.5,
                  maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap',
                }}>
                  {conflict.localText || <em style={{ color: '#888' }}>(empty)</em>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontFamily: 'Arial, sans-serif', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>
                  Attorney's version
                </div>
                <div style={{
                  padding: 10, border: '1px solid #bcd4ee', background: '#e8f0fb', borderRadius: 4,
                  fontFamily: 'Times New Roman, serif', fontSize: 13, lineHeight: 1.5,
                  maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap',
                }}>
                  {conflict.serverRow.answer_text || <em style={{ color: '#888' }}>(empty)</em>}
                </div>
              </div>
            </div>

            {conflict.mode === 'merge' && (
              <div className="form-group">
                <label>Edit the combined answer</label>
                <textarea value={conflict.mergeText}
                  onChange={e => setConflict(p => p ? { ...p, mergeText: e.target.value } : null)}
                  rows={8}
                  style={{ width: '100%', fontFamily: 'Times New Roman, serif', fontSize: 14, padding: 10, lineHeight: 1.5 }} />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {conflict.mode === 'pick' ? (
                <>
                  <button className="btn" onClick={() => setConflict(null)} disabled={saving}>Cancel</button>
                  <button className="btn" onClick={() => setConflict(p => p ? { ...p, mode: 'merge', mergeText: p.serverRow.answer_text || '' } : null)} disabled={saving}>
                    Edit and save
                  </button>
                  <button className="btn" onClick={resolveKeepTheirs} disabled={saving}>
                    Keep theirs
                  </button>
                  <button className="btn btn-primary" onClick={resolveKeepMine} disabled={saving}>
                    {saving ? <><span className="spinner" />Saving...</> : 'Keep mine'}
                  </button>
                </>
              ) : (
                <>
                  <button className="btn" onClick={() => setConflict(p => p ? { ...p, mode: 'pick' } : null)} disabled={saving}>
                    ← Back
                  </button>
                  <button className="btn btn-primary" onClick={resolveMerge} disabled={saving}>
                    {saving ? <><span className="spinner" />Saving...</> : 'Save merged answer'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
